// Aba Estilo — o portão do edvid ENTRE as fases (renderSetup/setupGo, app.js ~1176):
// imagens geradas por IA (quantas; a IA decide atrás da pessoa × tela dividida por
// imagem — substitui o "Tipo de edição"), cor de destaque (antes dos estilos de
// texto, porque é com ela que eles pintam), headline + opções de headline, legenda,
// elementos, música e observação livre.
// A vantagem sobre o edvid: além dos cards animados, um @remotion/player toca a
// composição REAL sobre o cut.mp4 com o estilo escolhido aplicado, ao vivo.
import { useEffect, useMemo, useRef, useState } from 'react';
import type { EdvidJobState, StyleElements, StyleSetup } from '../../../types/videoEdit';
import type { VideoEditorMusicTrack } from '../../../types/videoEditor';
import { api } from '../../../services/api';
import {
  ACCENT_SWATCHES, CAPTION_OPTIONS, ELEMENT_OPTIONS, HEADLINE_OPTIONS, IMAGE_COUNT_MAX, IMAGE_CREDIT_COST, IMAGE_LAYOUTS,
  INTEGRAIA_ACCENT, accentWhere, clampImageCount, initialStyle, styleSummary,
} from './styleCatalog';
import { CaptionDemo, HeadlineDemo, NoneMark, STYLE_DEMO_CSS, useDemoFonts } from './StyleDemos';
import { applyStyle, baseDemoProps, withVideo } from './demoProps';
import { ReelsPreview } from './ReelsPreview';
import { normHex } from './edvidTime';
import { BTN_PRIMARY, INPUT, LABEL } from './ui';
import type { EdvidClient } from './useEdvidJob';

function Check() {
  return <svg viewBox="0 0 16 16" width={10} height={10} aria-hidden><path d="M3.5 8.5l3 3 6-7" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>;
}

function OptCard({ on, onClick, children, name, className = '', ...rest }: { on: boolean; onClick: () => void; children: React.ReactNode; name?: string; className?: string; 'aria-label'?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      aria-label={rest['aria-label']}
      title={rest['aria-label']}
      className={`relative flex flex-col items-center gap-1.5 rounded-2xl border p-1.5 transition-colors ${on ? 'border-indigo-400 bg-indigo-500/10' : 'border-white/10 bg-white/[0.02] hover:border-white/25'} ${className}`}
    >
      {children}
      {name && <span className={`text-[11px] font-bold ${on ? 'text-white' : 'text-slate-400'}`}>{name}</span>}
      <span className={`absolute right-1.5 top-1.5 flex h-4 w-4 items-center justify-center rounded-full border ${on ? 'border-indigo-400 bg-indigo-500 text-white' : 'border-white/20 bg-slate-950/60 text-transparent'}`}><Check /></span>
    </button>
  );
}

function Group({ title, note, children }: { title: string; note?: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <div className="flex items-baseline gap-3">
        <p className={LABEL}>{title}</p>
        {note && <span className="text-[11px] text-slate-500">{note}</span>}
      </div>
      {children}
    </section>
  );
}

function MusicPicker({ client, value, onChange }: { client: EdvidClient; value: string | null | undefined; onChange: (id: string | null) => void }) {
  const [q, setQ] = useState('');
  const [tracks, setTracks] = useState<VideoEditorMusicTrack[]>([]);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [unavailable, setUnavailable] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    setState('loading');
    const id = window.setTimeout(() => {
      client.listMusic(q.trim() || undefined)
        .then(r => { if (!alive) return; setTracks(r.tracks); setUnavailable(r.unavailable || null); setState('ready'); })
        .catch(() => { if (alive) setState('error'); });
    }, q ? 350 : 0);
    return () => { alive = false; window.clearTimeout(id); };
  }, [client, q]);
  return (
    <div className="rounded-2xl border border-white/10 bg-slate-950/50 p-3">
      <div className="flex items-center gap-2">
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar trilha no catálogo (vazio = em alta)" className={`${INPUT} text-xs`} />
        <button type="button" onClick={() => onChange(null)} className={`shrink-0 rounded-xl border px-3 py-2 text-[11px] font-black ${!value ? 'border-indigo-400 bg-indigo-500/15 text-indigo-200' : 'border-white/10 text-slate-400 hover:bg-white/5'}`}>IA escolhe</button>
      </div>
      {unavailable && <p className="mt-2 text-[11px] text-amber-300">Catálogo indisponível: {unavailable}</p>}
      {state === 'error' && <p className="mt-2 text-[11px] text-rose-300">Não foi possível carregar o catálogo — a IA escolhe a trilha.</p>}
      <ul className="mt-2 grid max-h-52 gap-1 overflow-y-auto sm:grid-cols-2">
        {state === 'loading' && <li className="text-[11px] text-slate-500">Carregando…</li>}
        {tracks.slice(0, 24).map(t => (
          <li key={t.audioId}>
            <button
              type="button"
              onClick={() => onChange(t.audioId)}
              className={`flex w-full items-center gap-2 rounded-xl border px-2 py-1.5 text-left ${value === t.audioId ? 'border-indigo-400 bg-indigo-500/10' : 'border-transparent hover:bg-white/5'}`}
            >
              {t.coverUrl ? <img src={t.coverUrl} alt="" className="h-8 w-8 shrink-0 rounded-lg object-cover" /> : <span className="h-8 w-8 shrink-0 rounded-lg bg-white/10" />}
              <span className="min-w-0">
                <span className="block truncate text-xs font-bold text-slate-100">{t.title || t.audioId}</span>
                <span className="block truncate text-[10px] text-slate-500">{t.artist || '—'}</span>
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export interface MusicSelectionLite { audioId: string; title?: string | null; artist?: string | null; volume?: number; enabled?: boolean }

/** Trilha do Instagram já anexada ao job: volume e liga/desliga pelo PATCH /jobs/:id/music (sem re-render). */
function CurrentMusic({ music, onPatch }: { music: MusicSelectionLite; onPatch: (p: { volume?: number; enabled?: boolean }) => Promise<void> }) {
  const [vol, setVol] = useState(Math.round((music.volume ?? 0.1) * 100));
  const [enabled, setEnabled] = useState(music.enabled !== false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  return (
    <div className="mb-2 flex flex-wrap items-center gap-3 rounded-2xl border border-lime-400/25 bg-lime-500/5 px-3 py-2">
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-bold text-lime-100">{music.title || music.audioId}{music.artist ? <span className="font-normal text-lime-200/70"> — {music.artist}</span> : null}</p>
        <p className="text-[10px] text-slate-500">Anexada ao Reel na publicação. O MP4 baixado sai sem ela.</p>
      </div>
      <label className="flex items-center gap-1.5 text-[11px] font-bold text-slate-300">
        <input type="checkbox" checked={enabled} onChange={e => { setEnabled(e.target.checked); void onPatch({ enabled: e.target.checked }); }} className="accent-indigo-500" />
        usar
      </label>
      <div className="flex items-center gap-2">
        <span className={LABEL}>vol</span>
        <input
          type="range" min={1} max={100} value={vol}
          onChange={e => {
            const v = Number(e.target.value);
            setVol(v);
            if (timer.current) clearTimeout(timer.current);
            timer.current = setTimeout(() => { void onPatch({ volume: v / 100 }); }, 500);
          }}
          className="w-24 accent-lime-400"
        />
        <span className="w-8 font-mono text-[10px] text-slate-400">{vol}%</span>
      </div>
    </div>
  );
}

/**
 * Opções de headline (POST /jobs/:id/hooks): busca uma vez ao abrir a aba quando
 * ainda não há texto escolhido, lista 3–4 cards selecionáveis e o "escrever o
 * meu". O texto vai para style.hookText — a prévia Remotion muda na hora.
 */
function HookOptions({ jobId, initial, value, onChange }: {
  jobId?: string;
  initial?: string[];
  value: string;
  onChange: (text: string) => void;
}) {
  const [options, setOptions] = useState<string[]>(() => (initial || []).filter(Boolean));
  const [state, setState] = useState<'idle' | 'loading' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const alive = useRef(true);
  const autoTried = useRef(false);
  useEffect(() => () => { alive.current = false; }, []);

  const fetchOptions = async () => {
    if (!jobId) return;
    setState('loading');
    setError(null);
    try {
      const r = await api.getEdvidHookOptions(jobId);
      if (!alive.current) return;
      const next = (r.options || []).map(o => String(o || '').trim()).filter(Boolean);
      setOptions(next);
      setState(next.length ? 'idle' : 'error');
      if (!next.length) setError('A IA não devolveu opções. Tente de novo ou escreva a sua.');
    } catch (e: any) {
      if (!alive.current) return;
      setState('error');
      setError(e?.message || 'Não foi possível gerar as opções agora.');
    }
  };

  // uma busca automática por abertura da aba, só se nada foi escolhido ainda
  useEffect(() => {
    if (autoTried.current || !jobId || value.trim() || options.length) return;
    autoTried.current = true;
    void fetchOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const chosen = value.trim();
  const custom = chosen && !options.includes(chosen);
  return (
    <div className="space-y-2 rounded-2xl border border-white/10 bg-white/[0.02] p-3">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-xs font-black text-slate-200">Opções geradas pela IA</p>
        <span className="text-[11px] text-slate-500">nenhuma escolhida = a IA escreve na hora de gerar</span>
        {jobId && (
          <button
            type="button"
            onClick={() => void fetchOptions()}
            disabled={state === 'loading'}
            className="ml-auto rounded-xl border border-indigo-400/40 px-3 py-1.5 text-[11px] font-black text-indigo-200 hover:bg-indigo-500/10 disabled:opacity-50"
          >
            {state === 'loading' ? 'Gerando…' : options.length ? 'Gerar outras' : 'Gerar opções'}
          </button>
        )}
      </div>
      {state === 'loading' && !options.length && (
        <div className="grid gap-1.5 sm:grid-cols-2">
          {[0, 1, 2, 3].map(i => <div key={i} className="h-11 animate-pulse rounded-xl bg-white/5" />)}
        </div>
      )}
      {options.length > 0 && (
        <div className={`grid gap-1.5 sm:grid-cols-2 ${state === 'loading' ? 'opacity-50' : ''}`} role="radiogroup" aria-label="Opções de headline">
          {options.map(o => {
            const on = chosen === o;
            return (
              <button
                key={o}
                type="button"
                role="radio"
                aria-checked={on}
                onClick={() => onChange(on ? '' : o)}
                className={`flex items-start gap-2 rounded-xl border px-3 py-2 text-left text-xs font-bold transition-colors ${on ? 'border-indigo-400 bg-indigo-500/15 text-white' : 'border-white/10 text-slate-300 hover:border-white/25'}`}
              >
                <span className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${on ? 'border-indigo-400 bg-indigo-500 text-white' : 'border-white/25 text-transparent'}`}><Check /></span>
                <span className="min-w-0">{o}</span>
              </button>
            );
          })}
        </div>
      )}
      {error && <p className="text-[11px] text-rose-300">{error}</p>}
      <div>
        <p className="mb-1 text-[11px] font-bold text-slate-400">Escrever o meu {custom && <span className="text-indigo-300">· em uso</span>}</p>
        <input
          value={custom ? value : ''}
          onChange={e => onChange(e.target.value)}
          maxLength={120}
          placeholder="Ex.: Você está perdendo dinheiro em cada venda"
          className={INPUT}
        />
      </div>
    </div>
  );
}

/** Sem as partes da IA: soco = últimas 1–2 palavras; o resto em duas linhas (espelho de threePartsSimple). */
function splitThree(text: string): string[] {
  const words = text.trim().split(/\s+/).filter(Boolean);
  if (words.length <= 2) return [words[0] || '', '', words[1] || ''];
  const punchN = words.length >= 6 ? 2 : 1;
  const rest = words.slice(0, -punchN);
  const punch = words.slice(-punchN).join(' ');
  if (rest.length === 1) return [rest[0], '', punch];
  let best = 1;
  let bestDiff = Infinity;
  for (let i = 1; i < rest.length; i++) {
    const d = Math.abs(rest.slice(0, i).join(' ').length * 1.5 - rest.slice(i).join(' ').length);
    if (d < bestDiff) { bestDiff = d; best = i; }
  }
  return [rest.slice(0, best).join(' '), rest.slice(best).join(' '), punch];
}

const STACK_FIELDS = [
  { label: 'Linha 1 — forte, itálico', ph: 'sua máquina' },
  { label: 'Linha 2 — fina', ph: 'pode estar perdendo' },
  { label: 'Linha 3 — destaque (serifada, cor de destaque)', ph: 'um tempão' },
];

function StackedFields({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  const set = (i: number, t: string) => onChange([0, 1, 2].map(k => (k === i ? t : value[k] || '')));
  return (
    <div className="grid gap-2 rounded-2xl border border-white/10 bg-white/[0.02] p-3 sm:grid-cols-3">
      {STACK_FIELDS.map((f, i) => (
        <label key={i} className="block">
          <span className="mb-1 block text-[11px] font-bold text-slate-400">{f.label}</span>
          <input value={value[i] || ''} onChange={e => set(i, e.target.value)} maxLength={40} placeholder={`${f.ph} (IA preenche)`} className={INPUT} />
        </label>
      ))}
    </div>
  );
}

function QuestionFields({ value, onChange }: { value: { title?: string; text?: string }; onChange: (v: { title?: string; text?: string }) => void }) {
  return (
    <div className="grid gap-2 rounded-2xl border border-white/10 bg-white/[0.02] p-3 sm:grid-cols-[1fr_2fr]">
      <label className="block">
        <span className="mb-1 block text-[11px] font-bold text-slate-400">Título da caixinha</span>
        <input value={value.title || ''} onChange={e => onChange({ ...value, title: e.target.value })} maxLength={60} placeholder="O que você me perguntaria?" className={INPUT} />
      </label>
      <label className="block">
        <span className="mb-1 block text-[11px] font-bold text-slate-400">Pergunta (fica na tela o vídeo inteiro)</span>
        <input value={value.text || ''} onChange={e => onChange({ ...value, text: e.target.value })} maxLength={140} placeholder="A IA escreve a pergunta que o vídeo responde" className={INPUT} />
      </label>
    </div>
  );
}

export function StyleTab({ edvid, jobId, client, brandAccent, submitting, onSubmit, musicSelection, onMusicPatch }: {
  edvid: EdvidJobState;
  /** habilita "Opções de headline" (POST /jobs/:id/hooks) */
  jobId?: string;
  client: EdvidClient;
  brandAccent?: string | null;
  submitting: boolean;
  onSubmit: (style: StyleSetup) => void;
  musicSelection?: MusicSelectionLite | null;
  onMusicPatch: (patch: { volume?: number; enabled?: boolean }) => Promise<void>;
}) {
  const cut = edvid.cut!;
  // cor da marca: a do job (backend lê visualTokens.colors) e, na falta, a da tela que abriu
  const brandHex = normHex(edvid.brand?.accent || brandAccent || '') || null;
  const [style, setStyle] = useState<StyleSetup>(() => initialStyle(edvid.visual?.style || edvid.pendingStyle, brandHex));
  const [fontsVersion, setFontsVersion] = useState(0);
  const [hexDraft, setHexDraft] = useState(style.accent.slice(1).toUpperCase());
  useDemoFonts(() => setFontsVersion(v => v + 1));
  const hasVisual = !!edvid.visual;
  const rendering = edvid.phase === 'visual_rendering';

  const set = (patch: Partial<StyleSetup>) => setStyle(s => ({ ...s, ...patch }));
  const setEl = (id: keyof StyleElements) => setStyle(s => ({ ...s, elements: { ...s.elements, [id]: !s.elements[id] } }));
  const setAccent = (hex: string) => { const n = normHex(hex); if (n) { set({ accent: n }); setHexDraft(n.slice(1).toUpperCase()); } };

  // preview: as props da Fase 2 quando existem (legenda real), senão o demo a partir das falas
  const base = useMemo(() => edvid.visual?.props || baseDemoProps(cut), [edvid.visual?.props, cut]);
  const previewProps = useMemo(() => withVideo(applyStyle(base, style), cut.url), [base, style, cut.url]);
  const where = accentWhere(style);
  const imageCount = clampImageCount(style.imageCount);
  // preço efetivo por imagem (superadmin pode sobrescrever): nunca fixo no código
  const [imageCost, setImageCost] = useState<number>(IMAGE_CREDIT_COST);
  useEffect(() => {
    let alive = true;
    api.getCreditPricing()
      .then(r => { const c = Number(r?.data?.video_image_insert?.cost); if (alive && Number.isFinite(c)) setImageCost(c); })
      .catch(() => undefined);
    return () => { alive = false; };
  }, []);
  const fmtCredits = (n: number) => n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
  const swatches = [...new Set([INTEGRAIA_ACCENT, ...ACCENT_SWATCHES].map(c => normHex(c)).filter(c => c && c !== brandHex) as string[])];
  const isBrand = !!brandHex && style.accent === brandHex;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 lg:flex-row lg:overflow-hidden" style={{ ['--hl-accent' as any]: style.accent }}>
      <style>{STYLE_DEMO_CSS}</style>
      <div className="min-w-0 flex-1 space-y-6 lg:overflow-y-auto lg:pr-2">
        <div>
          <h2 className="text-lg font-black text-white">Como vamos montar o visual?</h2>
          <p className="mt-0.5 text-xs text-slate-400">Cada card mostra o próprio estilo. O player ao lado roda a composição real sobre o seu corte.</p>
        </div>

        <Group title="Imagens geradas por IA" note={imageCount ? `${fmtCredits(imageCost)} crédito por imagem entregue` : 'nenhuma imagem'}>
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-3">
            <div className="flex flex-wrap items-start gap-4">
              <div className="min-w-0 flex-1 space-y-2.5">
                <p className="text-[11px] leading-relaxed text-slate-400">
                  Escolha quantas imagens o vídeo recebe. A IA gera cada uma a partir do que você está falando e decide onde ela entra:
                  <b className="text-slate-200"> atrás de você</b> (um card entre o fundo e você recortado) ou em <b className="text-slate-200">tela dividida</b> (faixa em cima ou embaixo, quando a imagem precisa ser lida).
                </p>
                <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Quantidade de imagens geradas por IA">
                  {Array.from({ length: IMAGE_COUNT_MAX + 1 }, (_, n) => (
                    <button
                      key={n}
                      type="button"
                      role="radio"
                      aria-checked={imageCount === n}
                      onClick={() => set({ imageCount: n, edit: 'limpa' })}
                      className={`h-8 min-w-8 rounded-lg border px-2.5 text-xs font-black transition-colors ${imageCount === n ? 'border-indigo-400 bg-indigo-500/20 text-white' : 'border-white/10 text-slate-400 hover:border-white/25 hover:text-slate-200'}`}
                    >
                      {n === 0 ? 'Nenhuma' : n}
                    </button>
                  ))}
                </div>
                {imageCount > 0 && (
                  <p className="text-[11px] leading-snug text-amber-300">
                    {fmtCredits(imageCost)} crédito por imagem entregue — {fmtCredits(imageCount * imageCost)} créditos pelas {imageCount} imagens. Em comparações, duas imagens podem aparecer lado a lado.
                  </p>
                )}
              </div>
              <div className={`flex gap-2 ${imageCount ? '' : 'opacity-40'}`}>
                {IMAGE_LAYOUTS.map(l => (
                  <div key={l.id} className="flex w-[92px] flex-col items-center gap-1 text-center" title={l.hint}>
                    <span className="block h-[118px] w-[66px] [&>svg]:h-full [&>svg]:w-full" dangerouslySetInnerHTML={{ __html: l.mock }} />
                    <span className="text-[11px] font-bold text-slate-300">{l.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Group>

        <Group title="Animações" note="contador, checklist, linha do tempo… a IA escolhe onde significam algo">
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-3">
            <p className="mb-2 text-[11px] leading-relaxed text-slate-400">
              Até quantas animações a IA pode colocar, sincronizadas com o que você fala (números viram contador, listas viram checklist, processos viram linha do tempo). Ela pode usar menos. Você edita tudo na aba Final.
            </p>
            <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Quantidade de animações">
              {[0, 1, 2, 3, 4, 5, 6].map(n => {
                const on = (style.graphicsCount ?? 2) === n;
                return (
                  <button
                    key={n}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    onClick={() => set({ graphicsCount: n })}
                    className={`h-8 min-w-8 rounded-lg border px-2.5 text-xs font-black transition-colors ${on ? 'border-indigo-400 bg-indigo-500/20 text-white' : 'border-white/10 text-slate-400 hover:border-white/25 hover:text-slate-200'}`}
                  >
                    {n === 0 ? 'Nenhuma' : n}
                  </button>
                );
              })}
            </div>
          </div>
        </Group>

        <Group title="Cor de destaque" note={where.length ? `aplicada ${where.join(' e ')}` : 'os estilos escolhidos não usam destaque'}>
          <div className="flex flex-wrap items-center gap-2">
            {brandHex && (
              <button
                type="button"
                onClick={() => setAccent(brandHex)}
                title={`Cor da marca ${brandHex.toUpperCase()}`}
                aria-pressed={isBrand}
                className={`flex h-9 items-center gap-2 rounded-full border py-1 pl-1 pr-3 text-[11px] font-black transition-colors ${isBrand ? 'border-indigo-400 bg-indigo-500/15 text-white ring-1 ring-indigo-400/40' : 'border-white/15 text-slate-300 hover:border-white/30'}`}
              >
                <span className="h-7 w-7 rounded-full border-2 border-white/30" style={{ background: brandHex }} />
                Cor da marca
                {isBrand && <span className="text-indigo-300"><Check /></span>}
              </button>
            )}
            {swatches.map(c => (
              <button
                key={c}
                type="button"
                onClick={() => setAccent(c)}
                title={c === INTEGRAIA_ACCENT ? `IntegraIA ${c.toUpperCase()}` : c.toUpperCase()}
                className={`h-8 w-8 rounded-full border-2 transition-transform hover:-translate-y-0.5 ${style.accent === c ? 'border-white ring-2 ring-indigo-400 ring-offset-2 ring-offset-[#070b14]' : 'border-white/20'}`}
                style={{ background: c }}
              />
            ))}
            <label className="relative h-8 w-8 cursor-pointer overflow-hidden rounded-full border-2 border-white/20" title="Escolher cor" style={{ background: 'conic-gradient(red, yellow, lime, cyan, blue, magenta, red)' }}>
              <input type="color" value={style.accent} onChange={e => setAccent(e.target.value)} className="absolute inset-0 cursor-pointer opacity-0" aria-label="Escolher cor" />
            </label>
            <div className={`flex items-center rounded-xl border bg-slate-950 px-2 ${normHex(hexDraft) || !hexDraft ? 'border-white/10' : 'border-rose-400/60'}`}>
              <span className="font-mono text-xs text-slate-500">#</span>
              <input
                value={hexDraft}
                maxLength={7}
                spellCheck={false}
                onChange={e => { setHexDraft(e.target.value.replace(/^#/, '').toUpperCase()); const n = normHex(e.target.value); if (n) set({ accent: n }); }}
                onBlur={() => setHexDraft(style.accent.slice(1).toUpperCase())}
                className="w-16 bg-transparent py-1.5 pl-1 font-mono text-xs text-slate-100 outline-none"
                aria-label="Cor de destaque em hexadecimal"
              />
            </div>
          </div>
        </Group>

        <Group title="Headline">
          <div className="flex flex-wrap items-start gap-2.5">
            {HEADLINE_OPTIONS.map(o => (
              <OptCard key={o.id} on={style.headline === o.id} onClick={() => set({ headline: o.id })} aria-label={o.name}>
                <div className="flex h-16 w-[170px] items-center justify-center overflow-hidden rounded-xl bg-[radial-gradient(circle_at_50%_30%,#1e293b,#0b1020)]">
                  {o.id === 'none' ? <span className="flex flex-col items-center gap-1"><NoneMark /><span className="text-[10px] font-bold text-slate-400">{o.name}</span></span> : <HeadlineDemo id={o.id} fontsVersion={fontsVersion} />}
                </div>
              </OptCard>
            ))}
          </div>
        </Group>

        {style.headline !== 'none' && (
          <Group title="HEADLINE" note="o que ficar vazio a IA preenche ao gerar">
            {style.headline === 'pergunta' ? (
              <QuestionFields value={style.hookQuestion || {}} onChange={hookQuestion => set({ hookQuestion })} />
            ) : (
              <>
                <HookOptions
                  jobId={jobId}
                  initial={edvid.visual?.hookOptions}
                  value={style.hookText || ''}
                  onChange={hookText => set(style.headline === 'empilhado' ? { hookText, hookParts: hookText ? splitThree(hookText) : [] } : { hookText })}
                />
                {style.headline === 'empilhado' && (
                  <StackedFields value={style.hookParts || []} onChange={hookParts => set({ hookParts })} />
                )}
              </>
            )}
          </Group>
        )}

        <Group title="Legenda">
          <div className="flex flex-wrap items-start gap-2.5">
            {CAPTION_OPTIONS.map(o => (
              <OptCard key={o.id} on={style.captions === o.id} onClick={() => set({ captions: o.id })} aria-label={o.name}>
                <div className="flex h-16 w-[170px] items-center justify-center overflow-hidden rounded-xl bg-[radial-gradient(circle_at_50%_30%,#1e293b,#0b1020)]">
                  {o.id === 'none' ? <span className="flex flex-col items-center gap-1"><NoneMark /><span className="text-[10px] font-bold text-slate-400">{o.name}</span></span> : <CaptionDemo id={o.id} fontsVersion={fontsVersion} />}
                </div>
              </OptCard>
            ))}
          </div>
        </Group>

        <Group title="Elementos da edição" note="desmarcado = não entra">
          <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
            {ELEMENT_OPTIONS.map(e => {
              const on = !!style.elements[e.id];
              return (
                <button
                  key={e.id}
                  type="button"
                  onClick={() => setEl(e.id)}
                  aria-pressed={on}
                  className={`flex items-center gap-2.5 rounded-xl border px-3 py-2.5 text-left transition-colors ${on ? 'border-indigo-400/70 bg-indigo-500/10' : 'border-white/10 hover:border-white/25'}`}
                >
                  <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${on ? 'border-indigo-400 bg-indigo-500 text-white' : 'border-white/25 text-transparent'}`}><Check /></span>
                  <span className={`h-4 w-4 shrink-0 ${on ? 'text-indigo-300' : 'text-slate-500'} [&>svg]:h-full [&>svg]:w-full`} dangerouslySetInnerHTML={{ __html: e.icon }} />
                  <span className="min-w-0">
                    <span className={`block text-xs font-bold ${on ? 'text-white' : 'text-slate-300'}`}>{e.name}</span>
                    <span className="block text-[10px] text-slate-500">{e.hint}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </Group>

        {style.elements.music && (
          <Group title="Música do Instagram na publicação" note="trilha do catálogo anexada ao publicar — não é mixada no vídeo">
            {musicSelection?.audioId && <CurrentMusic music={musicSelection} onPatch={onMusicPatch} />}
            <MusicPicker client={client} value={style.musicTrackId} onChange={id => set({ musicTrackId: id })} />
          </Group>
        )}

        <Group title="Observação livre">
          <textarea
            value={style.note || ''}
            onChange={e => set({ note: e.target.value })}
            rows={2}
            placeholder="O que a IA deve saber para o visual (opcional). Ex.: “sem zoom na parte da tela do sistema”."
            className={`${INPUT} resize-none`}
          />
        </Group>

        <div className="sticky bottom-0 -mx-1 flex flex-wrap items-center gap-3 border-t border-white/10 bg-[#070b14]/95 px-1 py-3 backdrop-blur">
          <p className="min-w-0 flex-1 text-[11px] text-slate-400">{styleSummary(style)}</p>
          <button type="button" className={BTN_PRIMARY} disabled={submitting || rendering} onClick={() => onSubmit({
            ...style,
            edit: 'limpa',
            imageCount,
            graphicsCount: style.graphicsCount ?? 2,
            hookText: style.hookText?.trim() || undefined,
            hookParts: style.headline === 'empilhado' ? [0, 1, 2].map(i => style.hookParts?.[i]?.trim() || '') : undefined,
            hookQuestion: style.headline === 'pergunta' ? { title: style.hookQuestion?.title?.trim() || '', text: style.hookQuestion?.text?.trim() || '' } : undefined,
            note: style.note?.trim() || undefined,
          })}>
            {rendering ? 'Gerando visual…' : submitting ? 'Enviando…' : hasVisual ? 'Refazer visual' : 'Gerar visual'}
          </button>
        </div>
      </div>

      <aside className="flex shrink-0 flex-col items-center gap-2 lg:w-[min(34vw,420px)]">
        <p className={`${LABEL} self-start`}>Prévia ao vivo · composição real</p>
        <div className="relative w-full max-w-[300px] overflow-hidden rounded-2xl border border-white/10 bg-black lg:max-w-none" style={{ aspectRatio: '9 / 16', maxHeight: 'calc(100dvh - 190px)' }}>
          <ReelsPreview props={previewProps} loop autoPlay controls maxSeconds={Math.min(cut.durationSec, 14)} />
        </div>
        <p className="text-center text-[10px] text-slate-500">
          {edvid.visual ? 'Legendas da Fase 2 com o estilo novo aplicado.' : 'Legendas provisórias a partir das falas dos takes — a Fase 2 retranscreve o corte.'}
        </p>
      </aside>
    </div>
  );
}
